import type { BrandColor } from '@app-types/color'
import classNames from 'classnames'
import type { FunctionComponent } from 'react'

import styles from './spinner.module.scss'

/** Displays a colourful (or not, up to you) spinner,
 * so loading stuff is less boring for the user.
 * If you know the size and form of your content, use skeleton instead.  */
export const Spinner: FunctionComponent<React.PropsWithChildren<Props>> = ({
    size = 'medium',
    color = 'step-white',
    ...restOfProps
}) => {
    const isMulti = color === 'multi'

    return (
        <>
            &nbsp;
            <div
                data-testid='progress.wrapper'
                aria-busy='true'
                title='Loading…'
                className={classNames(
                    styles.spinner,
                    styles[`spinner-${size}`],
                )}
                {...restOfProps}
            >
                <svg
                    className={styles.inner}
                    viewBox='0 0 24 24'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                >
                    {/* Background ring */}
                    <path
                        className={styles.backgroundRing}
                        d='M15.4868 3.06271C16.7018 3.53146 17.8126 4.23493 18.7557 5.13296C19.6988 6.03098 20.4558 7.10597 20.9835 8.29655C21.5112 9.48714 21.7992 10.77 21.8311 12.0719C21.863 13.3738 21.6381 14.6692 21.1694 15.8842C20.7006 17.0992 19.9971 18.2099 19.0991 19.153C18.2011 20.0962 17.1261 20.8532 15.9355 21.3808C14.7449 21.9085 13.4621 22.1965 12.1602 22.2284C10.8583 22.2603 9.56289 22.0354 8.3479 21.5667C7.13291 21.0979 6.02215 20.3945 5.07903 19.4964C4.13591 18.5984 3.37891 17.5234 2.85124 16.3328C2.32357 15.1423 2.03557 13.8594 2.00368 12.5575C1.97179 11.2556 2.19664 9.96021 2.66539 8.74522C3.13414 7.53023 3.83761 6.41947 4.73564 5.47635C5.63366 4.53323 6.70865 3.77623 7.89923 3.24856C9.08982 2.72089 10.3727 2.43289 11.6746 2.401C12.9764 2.36911 14.2719 2.59396 15.4868 3.06271L15.4868 3.06271Z'
                        strokeWidth='3.5'
                        stroke='rgba(10, 10, 10, 0.15)'
                    />
                    {/* Animated spinner */}
                    <path
                        data-testid='progress.circle'
                        className={classNames(
                            styles.circle,
                            isMulti && styles.circleMulti,
                        )}
                        style={
                            isMulti ? undefined : { stroke: `var(--${color})` }
                        }
                        d='M15.4868 3.06271C16.7018 3.53146 17.8126 4.23493 18.7557 5.13296C19.6988 6.03098 20.4558 7.10597 20.9835 8.29655C21.5112 9.48714 21.7992 10.77 21.8311 12.0719C21.863 13.3738 21.6381 14.6692 21.1694 15.8842C20.7006 17.0992 19.9971 18.2099 19.0991 19.153C18.2011 20.0962 17.1261 20.8532 15.9355 21.3808C14.7449 21.9085 13.4621 22.1965 12.1602 22.2284C10.8583 22.2603 9.56289 22.0354 8.3479 21.5667C7.13291 21.0979 6.02215 20.3945 5.07903 19.4964C4.13591 18.5984 3.37891 17.5234 2.85124 16.3328C2.32357 15.1423 2.03557 13.8594 2.00368 12.5575C1.97179 11.2556 2.19664 9.96021 2.66539 8.74522C3.13414 7.53023 3.83761 6.41947 4.73564 5.47635C5.63366 4.53323 6.70865 3.77623 7.89923 3.24856C9.08982 2.72089 10.3727 2.43289 11.6746 2.401C12.9764 2.36911 14.2719 2.59396 15.4868 3.06271L15.4868 3.06271Z'
                        strokeWidth='3.5'
                    />
                </svg>
            </div>
        </>
    )
}

interface Props {
    /** Color of the spinner. `multi` option rotates primary brand colors. */
    color?: BrandColor | 'multi'
    size?: 'small' | 'medium' | 'large'
}
